import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface BankState {
	autoAccept: boolean | undefined
	bankContact: undefined | string
	bankId: string | undefined
	bankImage: undefined | File
	bankName: string | undefined
	checkIban: boolean
	createdBy: string | undefined
	creationDate: string | undefined
	deleted: boolean | undefined
	enabled: boolean | undefined
	id: number | undefined
	modificationDate: undefined | string
	modifiedBy: undefined | string
	primaryColor: undefined | string
	repositoryName: string | undefined
	secondaryColor: undefined | string
	timeToExpiry: undefined | number
	version: number | undefined
	whatsapp: boolean | undefined
}

export interface PageableElement {
	content: BankState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: BankState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface BanksListResponse {
	nextUrl: null | string
	page: PageElement
}

export interface BanksListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: BanksListResponse | undefined
}

export interface AllBanksListState {
	loading: boolean
	error: undefined | AxiosError<string>
	list: BankEssential[]
	selected: BankEssential | undefined
}

export interface BanksPayload {
	filter?: any | undefined
	sort?: any | undefined
	page?: number | undefined
	pageSize?: number | undefined
}

export interface BanksEssentialPayload {
	filter?: any | undefined
	sort?: any | undefined
}

export interface FetchBanksPayload {
	apiName: string
	url: string
}

export interface BankEssential {
	id: string
	name: string
	img: undefined | string
	primaryColor: undefined | string
	secondaryColor: undefined | string
}

export interface ICreateResponse {}

interface IPatchPayload {
	idBank: string
	bankData: BankState
}

let abortController: AbortController | null = null

/*
const axiosInstance = axios.create();

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.REACT_APP_BANK_MANAGEMENT_API_URL;*/

export const getUserAllBanks = createAsyncThunk<
	BankEssential[],
	BanksEssentialPayload,
	{ rejectValue: string }
>(
	"banks/getUserAllBanks",
	async ({ filter, sort }: BanksEssentialPayload, { rejectWithValue }) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apibank/v2/getUserAllBanks`
			const parameters = new URLSearchParams()

			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<BankEssential[]> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)

			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<BanksListResponse>

			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const getBanksPaginated = createAsyncThunk<
	BanksListResponse,
	BanksPayload,
	{ rejectValue: string }
>(
	"banks/getBanksPaginated",
	async (
		{ page, pageSize, filter, sort }: BanksPayload,
		{ rejectWithValue }
	) => {
		try {
			// Annulla la richiesta precedente se ancora in corso
			if (abortController) {
				abortController.abort()
			}

			// Crea nuovo controller
			abortController = new AbortController()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apibank/v2/getUserBanksPaginated`
			const parameters = new URLSearchParams()

			if (page === 0 || page) {
				parameters.append("page", page.toString())
			}
			if (pageSize) {
				parameters.append("size", pageSize.toString())
			}
			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}

			const response: AxiosResponse<BanksListResponse> = await axios.get(
				`${baseUrl}${parameters.size > 0 ? "?" + parameters.toString() : ""}`,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<BanksListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const fetchBanks = createAsyncThunk<
	BanksListResponse,
	FetchBanksPayload,
	{ rejectValue: string }
>(
	"banks/fetchBanks",
	async (
		{ apiName, url }: FetchBanksPayload,
		{ dispatch, rejectWithValue }
	) => {
		try {
			// Annulla la richiesta precedente se ancora in corso
			if (abortController) {
				abortController.abort()
			}

			// Crea nuovo controller
			abortController = new AbortController()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse<BanksListResponse> = await axios.get(
				url,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<BanksListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const updateBank = createAsyncThunk<
	ICreateResponse,
	IPatchPayload,
	{ rejectValue: AxiosError }
>(
	"bank/patch",
	async ({ bankData, idBank }: IPatchPayload, { rejectWithValue }) => {
		try {
			const formData = new FormData()
			Object.keys(bankData).forEach((key) => {
				formData.append(key, bankData[key])
			})

			/*
        if (bankData.bankImage) {
            formData.append('bankImage', bankData.bankImage);
        }*/
			const headerConfig: AxiosRequestConfig = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				withCredentials: true,
			}
			const response: AxiosResponse = await axios.patch(
				`${process.env.REACT_APP_BANK_MANAGEMENT_API_URL}/corporateservice/apibank/v2/update/${idBank}`,
				formData,
				headerConfig
			)
			return response
		} catch (error) {
			return rejectWithValue(error?.response)
		}
	}
)
